import { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';
import { Typography } from '@mui/material';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { formatDate, isValidFile } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { AlertModal, IAlertMessage } from '@shared/components/modals/AlertModal';
import { PageWrapper } from '../../../../shared/styledComponents';
import { FileUploadForm } from '../shared/UploadFileForm';
import { IDealMoveImportResponse, IFileUpload, ResponseType } from '../shared/interfaces';
import { requestFundedDateDealMoveImport } from './requests';

export interface IChangeFundedDate extends IFileUpload {
  fundedDate: string;
}
const excelExtensions = ['.csv', '.xlsx', '.xls', '.ods'];

const changeFundedDateSchema = Yup.object().shape({
  document: Yup.mixed().test('required', 'Please select a valid csv or excel file', (file: File) => isValidFile(file, excelExtensions)),
  fundedDate: Yup.date().typeError('Funded Date is invalid').required(),
});

const initValues: IChangeFundedDate = {
  document: new File([], ''),
  fundedDate: '',
};

export const validateDealMoveImportResponse = (response: IDealMoveImportResponse[]) => {
  let dealMoveImportResponse: IAlertMessage[] = [];
  if (response.length === 1) {
    dealMoveImportResponse = [
      {
        subtitle: response[0].messages,
        listItem: response[0].messages.includes(ResponseType.Successfull) ? [] : response[0].dealIds ?? [],
      },
    ];
  } else {
    dealMoveImportResponse = response.map((responseMessage) => {
      return {
        subtitle: responseMessage.messages,
        listItem: responseMessage.dealIds,
      };
    });
  }
  return dealMoveImportResponse;
};

export const ChangeFundedDate = () => {
  const [changeRequest, setChangeRequest] = useState<IChangeFundedDate>({} as IChangeFundedDate);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessages, setAlertMessages] = useState<IAlertMessage[]>([]);

  const handleSubmit = useCallback((data: IChangeFundedDate) => {
    setChangeRequest(data);
    setShowConfirmation(true);
  }, []);

  const changeFundedDateForm = [
    {
      width: '30%',
      name: 'fundedDate',
      label: 'Funded Date',
      component: SelectDateField,
    },
  ];

  const handleModelCancel = useCallback(() => setShowConfirmation(false), []);

  const handleModelConfirm = useCallback(async() => {
    setShowConfirmation(false);
    setIsLoading(true);
    const formattedFundedDate = moment(changeRequest.fundedDate).utcOffset(0, true).format();
    const response = await requestFundedDateDealMoveImport(changeRequest.document, formattedFundedDate);
    setIsLoading(false);
    if (response) {
      const formattedResponse = validateDealMoveImportResponse(response);
      setAlertMessages(formattedResponse);
      setShowAlertModal(true);
    } else {
      setAlertMessages([]);
      setShowAlertModal(false);
    }
  }, [changeRequest]);

  return (
    <PageWrapper>
      <LoaderBackdrop open={isLoading} handleClose={() => setIsLoading(false)} />
      <Typography variant="h3" sx={{ pr: '10px' }}>
        Change Funded Date
      </Typography>
      <Formik enableReinitialize initialValues={initValues} validationSchema={changeFundedDateSchema} onSubmit={handleSubmit}>
        {(props) => (
          <Form onSubmit={props.handleSubmit}>
            <FileUploadForm<IChangeFundedDate> value={props.values} setFieldValue={props.setFieldValue} formConfig={changeFundedDateForm} />
          </Form>
        )}
      </Formik>
      {showConfirmation && (
        <AlertModal 
          open 
          title={' '}
          subtitle={`Confirm Move 'Funded Date' to ${formatDate(changeRequest.fundedDate)}?`}
          buttonText="Yes" 
          cancelButtonText="No" 
          onClose={handleModelCancel} 
          onConfirm={handleModelConfirm}
        />
      )}
      {showAlertModal && 
        <AlertModal
          hideCancel
          title={' '}
          open={showAlertModal}
          alertMessages={alertMessages}
          buttonText={'Ok'}
          width="500px"
          onConfirm={() => setShowAlertModal(false)}
          onClose={() => setShowAlertModal(false)}
        />
      }
    </PageWrapper>
  );
};
